import React, { useEffect, useRef } from 'react';
import xIcon from '../img/footer/x.svg';
import linkedIn from '../img/footer/linkedinlogo.svg';
import instagram from '../img/footer/instagramlogo.svg';
import tikTok from '../img/footer/tiktoklogo.svg';
import facebook from '../img/footer/facebooklogo.svg';
import pizzaBoxes from '../img/pizza-boxes.svg';
import LiveChatBubble from '../img/footer/LiveChatBubble.svg';
import youtube from '../img/footer/youtubelogo.svg';
import googlePlay from '../img/footer/google-play-seeklogo.svg';
import appStore from '../img/footer/apple-store-seeklogo.svg';
import { useConfig } from '../helpers/useConfig';
import { useMatch } from 'react-router-dom';
import { gtmEvent } from '../helpers/commonHelpers';
import { DomRefs } from '../types/RefTypes';
import { Customer } from 'src/types/ApplicationTypes';

const handleAppDownloadClick = (appType: string, url: string) => {
  const params = { appType: appType };
  gtmEvent('App_Download_Click', params);
  window.open(url, '_blank')
};

const handleSocialsClick = (socialType: string, url: string) => {
  const params = { socialType: socialType };
  gtmEvent('Socials_Click', params);
  window.open(url, '_blank')
};

interface FooterProps {
  inApp: boolean;
  isTabletScreen: boolean;
  menuHeaderAtTopOfPage: boolean;
  refs: React.MutableRefObject<DomRefs>;
  spacerRefName: string;
  refName: string;
  bannerLoaded: boolean;
  customer: Customer | null;
  onLogOut: any;
}

const Footer = (props: FooterProps) => {
  const { inApp, refs, isTabletScreen, bannerLoaded, customer, onLogOut, menuHeaderAtTopOfPage } = props;
  const config = useConfig();
  const isDelivery = useMatch('/delivery');
  const observerRef = useRef<IntersectionObserver | null>(null);
  const footerSpacerRef = refs.current[props.spacerRefName];
  const footerRef = refs.current[props.refName];
  useEffect(() => {
    if (!footerRef) return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
    const options = {
      rootMargin: `0px 0px 0px 0px`,
      threshold: Array.from({ length: 1001 }, (_, i) => i / 1000)
    };
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.target == footerRef) {
          if (footerSpacerRef && footerSpacerRef instanceof HTMLElement) {
            if (!menuHeaderAtTopOfPage) {
              footerSpacerRef.style.height = entry.isIntersecting ? '0px' : (refs.current.headerRef?.clientHeight ?? 0) + (refs.current.bannerRef?.clientHeight ?? 0) + 'px';
            } else {
              footerSpacerRef.style.height = entry.isIntersecting ? '0px' : (refs.current.bannerRef?.clientHeight ?? 0) + 'px';
            }
          }
          if (!menuHeaderAtTopOfPage && refs.current.headerRef) {
            refs.current.headerRef.style.position = entry.isIntersecting ? 'relative' : 'fixed';
            refs.current.headerRef.style.bottom = entry.isIntersecting ? '0px' : (refs.current.bannerRef?.clientHeight ?? 0) + 'px';
          }
          if (refs.current.bannerRef) {
            refs.current.bannerRef.style.position = entry.isIntersecting ? 'relative' : 'fixed';
          }
        }
      });
    }, options);

    if (observerRef.current && footerRef && footerRef instanceof HTMLElement) {
      observerRef.current.observe(footerRef);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [refs, config, isTabletScreen, menuHeaderAtTopOfPage.valueOf, footerRef, footerSpacerRef, bannerLoaded]);

  const year = new Date();
  if (!config) return <></>;

  if (config.BRAND === 'PE') {
    return (
      <>
        <footer ref={r => refs.current[props.refName] = r} className="footer has-text-white has-background-primary" style={{ zIndex: 999 }}>
          <div className='is-max-widescreen'>
            <section className="section is-footer m-">
              <div className="columns is-flex-wrap-wrap" style={{ position: 'relative', maxWidth: '1440px', width: '100%' }}>
                <div className="column is-12" >
                  <div className="content">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isDelivery ? 'space-between' : 'flex-start', position: 'relative' }}>
                      <div className={`social-buttons-container ${isDelivery ? 'make-space-for-help' : ''}`}>
                        <img alt="Facebook Link" src={facebook} style={{ cursor: 'pointer' }} onClick={() => handleSocialsClick('facebook', 'https://www.facebook.com/pizzaexpress/')} />
                        <img alt="Instagram Link" src={instagram} style={{ cursor: 'pointer' }} onClick={() => handleSocialsClick('instagram', 'https://www.instagram.com/pizzaexpress/?hl=en')} />
                        <img alt="LinkedIn Link" src={linkedIn} style={{ cursor: 'pointer' }} onClick={() => handleSocialsClick('linkedin', 'https://www.linkedin.com/company/pizzaexpress')} />
                        <img alt="X Link" src={xIcon} style={{ cursor: 'pointer' }} onClick={() => handleSocialsClick('twitter', 'https://twitter.com/PizzaExpress?ref_src=twsrc%5Egoogle%7Ctwcamp%5Eserp%7Ctwgr%5Eauthor')} />
                        <img alt="YouTube Link" src={youtube} style={{ cursor: 'pointer' }} onClick={() => handleSocialsClick('youtube', 'https://www.youtube.com/user/PizzaExpressTube')} />
                        <img alt="TikTok Link" src={tikTok} style={{ cursor: 'pointer' }} onClick={() => handleSocialsClick('tiktok', 'https://www.tiktok.com/discover/pizzaexpress?lang=en')} />
                      </div>
                      {
                        isDelivery &&
                        <div role='deliveryHelp' style={{ position: 'absolute', right: 0, top: 10, height: 78, width: 81, transform: 'translateY(-50%)' }} onClick={() => window.open('https://www.pizzaexpress.com/deliveryhelp', '_blank')} >
                          <div style={{ cursor: 'pointer', position: 'relative', height: '100%', width: '100%' }}>
                            <p style={{ position: 'absolute', left: 17, top: 13, fontSize: '14px', textAlign: 'center', fontWeight: '200' }}>Delivery<br />help</p>
                            <img src={LiveChatBubble} alt='Live chat bubble' />
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>

                <div className="column is-12">
                  <div className="my-2" style={{ height: 1, backgroundColor: '#C4C4C4' }} />
                </div>

                <div className="column is-12">
                  <div className="content">
                    <div className="block">
                      <p style={{ fontSize: '1.25rem' }} className='title'>PizzaExpress Club App</p>
                    </div>
                    <div className="block">
                      <div className="app-stores level is-mobile is-justify-content-flex-start">
                        <div className="level-item is-narrow is-shrinkable">
                          <img alt="Apple App Store" className="image" src={appStore} style={{ cursor: 'pointer', width: '109px' }} onClick={() => handleAppDownloadClick('apple', 'https://apps.apple.com/gb/app/pizzaexpress/id440565075')} />
                        </div>
                        <div className="level-item is-narrow is-shrinkable app-icon">
                          <img alt="Google Play Store" className="image" src={googlePlay} style={{ cursor: 'pointer', width: '109px' }} onClick={() => handleAppDownloadClick('google', 'https://play.google.com/store/apps/details?id=com.pizzaexpress.appv2')} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="column is-12">
                  <div className="content">
                    <div className="level level-left level-list">
                      <div className="level-item is-narrow is-justify-content-flex-start">
                        <strong className="has-text-white">
                          <small>&copy; PizzaExpress {year.getFullYear()}</small>
                        </strong>
                      </div>
                      <div className="level-item is-narrow is-justify-content-flex-start">
                        <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.pizzaexpress.com/terms-and-conditions/online-ordering', '_blank')}>Terms & Conditions</small>
                      </div>
                      <div className="level-item is-narrow is-justify-content-flex-start">
                        <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.pizzaexpress.com/privacy-policy', '_blank')}>Privacy policy</small>
                      </div>
                      <div className="level-item is-narrow is-justify-content-flex-start">
                        <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.pizzaexpress.com/terms-of-use', '_blank')}>Terms of use</small>
                      </div>
                      <div className="level-item is-narrow is-justify-content-flex-start">
                        <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.pizzaexpress.com/cookie-policy', '_blank')}>Cookie policy</small>
                      </div>
                      <div className="level-item is-narrow is-justify-content-flex-start">
                        <small style={{ cursor: 'pointer' }} onClick={() => window.open(' https://www.pizzaexpress.com/help-and-contact', '_blank')}>Help & Contact</small>
                      </div>
                      {/* todo remove !inApp and pass didomi cookie out into user profile */}
                      {!inApp &&
                        <div className="level-item is-narrow is-justify-content-flex-start">
                          <small style={{ cursor: 'pointer' }} onClick={() => { window.Didomi.preferences.show(); }}>
                            Cookie Settings
                          </small>
                        </div>}
                      {customer && !inApp &&
                        < div className="level-item is-narrow is-justify-content-flex-start">
                          <small style={{ cursor: 'pointer' }} onClick={onLogOut}>Log out</small>
                        </div>
                      }
                    </div>
                  </div>
                </div>
                <img alt="pizza boxes" src={pizzaBoxes} style={{ position: 'absolute', right: '1rem', bottom: '25px' }} />
              </div>
            </section>
          </div >
        </footer >
      </>
    );
  }

  return (<>
    <footer ref={r => refs.current[props.refName] = r} className="has-text-white has-background-primary" style={{ zIndex: 999 }}>
      <div className={'is-max-widescreen'}>
        <section className="section is-footer">
          <div className="columns is-flex-wrap-wrap">

            <div className="column is-12">
              <div className="content">
                <div className="level level-left level-list">
                  <div className="level-item is-narrow is-justify-content-flex-start">
                    <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.milano.ie/about-us', '_blank')}>About Us</small>
                  </div>
                  <div className="level-item is-narrow is-justify-content-flex-start">
                    <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.milano.ie/help-and-contact', '_blank')}>Contact Us</small>
                  </div>
                  <div className="level-item is-narrow is-justify-content-flex-start">
                    <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.milano.ie/careers', '_blank')}>Careers</small>
                  </div>
                  <div className="level-item is-narrow is-justify-content-flex-start">
                    <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.milano.ie/privacy-policy', '_blank')}>Privacy policy</small>
                  </div>
                  <div className="level-item is-narrow is-justify-content-flex-start">
                    <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.milano.ie/Terms-and-Conditions/Click-and-Collect', '_blank')}>Terms And Conditions</small>
                  </div>
                  <div className="level-item is-narrow is-justify-content-flex-start">
                    <small style={{ cursor: 'pointer' }} onClick={() => window.open('https://www.milano.ie/cookie-policy', '_blank')}>Cookie policy</small>
                  </div>
                  <div className="level-item is-narrow is-justify-content-flex-start">
                    <small style={{ cursor: 'pointer' }} onClick={() => { window.Didomi.preferences.show(); }}>
                      Cookie Settings
                    </small>
                  </div>
                  {customer && !inApp &&
                    < div className="level-item is-narrow is-justify-content-flex-start">
                      <small style={{ cursor: 'pointer' }} onClick={onLogOut}>Log out</small>
                    </div>
                  }
                  <div className="level-item is-narrow is-justify-content-flex-start">
                    <small style={{ cursor: 'pointer' }}>&copy; Milano Limited {year.getFullYear()}</small>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>
      </div>
    </footer>


  </>);

};

export default Footer;